import { Helmet } from "react-helmet-async";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CookieConsent } from "@/components/CookieConsent";
import { Card, CardContent } from "@/components/ui/card";
import { Info, HelpCircle, ClipboardList, ArrowRight, BookOpen } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const CANONICAL = "https://eap-abutments.com/ti-base-oder-individuelles-abutment";
const LAST_UPDATED = "2026-09-15";

const de = {
  metaTitle: "Ti-Base oder individuelles Abutment? Unterschiede & Möglichkeiten | EAP®",
  metaDescription:
    "Was unterscheidet Ti-Base, individuelles Titan-Abutment und patientenspezifische Abutment-Konzepte? Neutrale Informationen zu Geometrie, Emergenzprofil, Materialien und Alternativen.",
  breadcrumb: "Abutment-Leitfaden",
  h1: "Ti-Base oder individuelles Abutment? Ein Leitfaden für Patienten",
  intro:
    "Wenn ein Zahnimplantat mit einer Krone versorgt wird, braucht es dazwischen ein Verbindungsteil: das Abutment. Für dieses Teil gibt es mehrere Bauformen und Materialien. Diese Seite erklärt die Unterschiede sachlich und ohne Wertung, damit Sie das Gespräch mit Ihrem Behandler besser führen können.",
  shortTitle: "Die kurze Antwort",
  shortAnswer:
    "Es gibt keine Bauform, die für alle Patienten die richtige ist. Ti-Bases mit aufgeklebter Krone, individuell gefertigte Abutments aus Titan oder Zirkonoxid und konfektionierte Standardabutments werden alle klinisch eingesetzt. Welche Variante für Sie passt, hängt von Implantatposition, Zahnfleischhöhe, Bisssituation, Ästhetikanspruch, Reinigbarkeit und dem Arbeitsablauf im Labor ab. Diese Entscheidung trifft Ihr Behandlungsteam gemeinsam mit Ihnen, nicht ein Hersteller.",
  basicsTitle: "Was ein Abutment ist",
  basics: [
    {
      term: "Implantat",
      text: "Die Schraube im Knochen, die die Zahnwurzel ersetzt. Sie ist im Mund nicht sichtbar.",
    },
    {
      term: "Abutment",
      text: "Das Verbindungsteil zwischen Implantat und Krone. Es bestimmt, wie die Krone im Zahnfleisch austritt.",
    },
    {
      term: "Emergenzprofil",
      text: "Die Form des Übergangs von der Implantatschulter zur sichtbaren Krone. Sie beeinflusst, wie das Zahnfleisch anliegt und wie gut die Stelle zu reinigen ist.",
    },
    {
      term: "Klebefuge",
      text: "Die Verbindungslinie, wenn zwei Teile miteinander verklebt werden – etwa Krone und Ti-Base. Wo diese Linie liegt, ist ein Konstruktionsmerkmal.",
    },
  ],
  typesTitle: "Die gängigen Bauformen",
  types: [
    {
      name: "Konfektioniertes Standardabutment",
      what: "Ein industriell vorgefertigtes Teil in festen Größen, im Labor begrenzt anpassbar.",
      notes:
        "Etabliert, gut verfügbar und kalkulierbar. Die Form folgt nicht der individuellen Zahnfleischkontur, weshalb bei anspruchsvoller Ästhetik Kompromisse entstehen können.",
    },
    {
      name: "Ti-Base mit verklebter Krone",
      what: "Ein kurzer Titankern, auf den die Krone – meist aus Zirkonoxid oder Lithiumdisilikat – im Labor oder in der Praxis aufgeklebt wird.",
      notes:
        "Sehr verbreitet in digitalen Arbeitsabläufen. Die Lage der Klebefuge, die Klebetechnik und die Überschussentfernung sind hier zentrale technische Themen. Vieles davon liegt in der Ausführung, nicht im Produkttyp.",
    },
    {
      name: "Individuelles Zirkonoxid-Abutment",
      what: "Ein patientenspezifisch gefrästes Keramikteil, häufig auf einer Titanbasis.",
      notes:
        "Wird oft aus ästhetischen Gründen bei dünnem Zahnfleisch gewählt. Keramik ist bruchempfindlicher als Titan; die Eignung hängt von Belastung und Position ab.",
    },
    {
      name: "Individuelles Titan-Abutment",
      what: "Ein patientenspezifisch gefertigtes Titanteil, dessen Geometrie am Einzelfall konstruiert wird. EAP® ist eine von mehreren Herstelleroptionen in dieser Gruppe.",
      notes:
        "Titan gilt als mechanisch robust und langjährig dokumentiert. Bei sehr dünnem Zahnfleisch kann Titan durchscheinen; das hängt von Gewebedicke und Positionierung ab.",
    },
  ],
  tableTitle: "Orientierungstabelle",
  tableNote:
    "Die Tabelle beschreibt typische Merkmale der Bauformen. Sie ist keine Rangliste und ersetzt keine individuelle Beurteilung.",
  tableHead: ["Merkmal", "Standardabutment", "Ti-Base + Krone", "Individuell Zirkonoxid", "Individuell Titan"],
  tableRows: [
    ["Form individuell anpassbar", "begrenzt", "über die Krone", "ja", "ja"],
    ["Material am Zahnfleisch", "Titan", "je nach Konstruktion", "Zirkonoxid", "Titan"],
    ["Klebefuge vorhanden", "meist nein", "ja", "je nach Aufbau", "je nach Aufbau"],
    ["Mechanische Robustheit", "hoch", "je nach Konstruktion", "materialabhängig", "hoch"],
    ["Digitaler Ablauf üblich", "teilweise", "ja", "ja", "ja"],
    ["Als definitiv geplant", "ja", "ja", "ja", "ja"],
  ],
  materialTitle: "Titan oder Zirkonoxid?",
  materialText:
    "Beide Materialien sind in der Implantatprothetik etabliert und beide haben dokumentierte Anwendungsbereiche. Titan ist mechanisch belastbar und seit Jahrzehnten dokumentiert. Zirkonoxid ist zahnfarben und wird häufig gewählt, wenn ein Durchscheinen vermieden werden soll. Eine allgemeine Überlegenheit eines Materials über das andere lässt sich aus der Literatur nicht ableiten; entscheidend sind Position, Belastung, Gewebedicke und Verarbeitung.",
  evidenceTitle: "Wie belastbar sind Aussagen zu diesem Thema?",
  evidenceIntro:
    "Nicht jede Aussage über Abutments hat das gleiche Gewicht. Diese drei Ebenen helfen beim Einordnen – auch bei Aussagen von Herstellern, uns eingeschlossen.",
  evidence: [
    {
      level: "Ebene A – klinische Studien",
      text: "Untersuchungen an Patienten mit Kontrollgruppen und definierten Endpunkten. Beispiel: Meta-Analysen zum marginalen Knochenverlust bei definitiven gegenüber mehrfach gewechselten Abutments berichten Unterschiede im Bereich von etwa 0,2 mm. Solche Werte sind Studienmittelwerte, keine Prognose für einen Einzelfall.",
    },
    {
      level: "Ebene B – Laborstudien und Beobachtungen",
      text: "Zellversuche, Materialprüfungen und klinische Beobachtungen ohne Vergleichsgruppe. Beispiel: In einer Untersuchung (Wilson 2009) wurden an 81 % der untersuchten Stellen mit Anzeichen einer periimplantären Erkrankung Zementreste gefunden. Das ist eine Assoziation, kein Kausalitätsnachweis.",
    },
    {
      level: "Ebene C – Konstruktions- und Herstellerangaben",
      text: "Beschreibungen davon, wie ein Produkt gebaut ist und welches Ziel die Konstruktion hat. Solche Angaben sind nachprüfbar, sagen aber allein nichts über klinische Ergebnisse aus.",
    },
  ],
  questionsTitle: "Acht Fragen an Ihr Behandlungsteam",
  questions: [
    "Welche Abutment-Bauform ist bei mir geplant und warum genau diese?",
    "Aus welchem Material besteht der Teil, der an meinem Zahnfleisch liegt?",
    "Gibt es eine Klebefuge, und wo liegt sie im Verhältnis zum Zahnfleischrand?",
    "Wird die Krone verklebt oder verschraubt, und was bedeutet das für späteren Zugang?",
    "Ist das Abutment als definitive Versorgung geplant oder wird es später gewechselt?",
    "Wie gut kann ich die Stelle zu Hause reinigen?",
    "Was passiert, wenn sich mein Zahnfleisch später zurückzieht?",
    "Welche Alternativen wären in meinem Fall ebenfalls möglich?",
  ],
  recessionTitle: "Wenn sich das Zahnfleisch später zurückzieht",
  recessionText:
    "Ein Zurückgehen des Zahnfleisches (Rezession) kann bei jeder Versorgungsart auftreten und hat viele Ursachen. Ob eine freiliegende Stelle nachträglich angepasst und poliert werden kann oder ob eine Neuanfertigung sinnvoller ist, hängt von der jeweiligen Konstruktion und der klinischen Situation ab. Diese Beurteilung trifft Ihr Behandler.",
  transparencyTitle: "Transparenz zur Quelle",
  transparencyText:
    "Diese Seite wird von der EAP® Produktions- und Patentverwertungs-GmbH bereitgestellt, einem Hersteller patientenindividueller Titan-Abutments. Wir haben ein wirtschaftliches Interesse an unserem Produkt. Deshalb sind Angaben zu EAP® auf dieser Seite als Konstruktionsmerkmale gekennzeichnet und nicht als klinischer Vorteil formuliert. Wir behaupten weder eine Überlegenheit gegenüber anderen Systemen noch eine Vorbeugung von Erkrankungen.",
  linksTitle: "Weiterlesen",
  links: [
    { href: "/ai-press#third-opinion", label: "Die 3. Meinung: 20 Patientenfragen zu Implantat-Abutments" },
    { href: "/fuer-patienten", label: "Für Patienten: Grundlagen verständlich erklärt" },
    { href: "/research", label: "Literaturübersicht: verwendete Fachpublikationen" },
    { href: "/faq", label: "Häufige Fragen zu EAP® Abutments" },
  ],
  disclaimerTitle: "Hinweis",
  disclaimerText:
    "Diese Seite dient der allgemeinen Information und ersetzt keine zahnmedizinische Beratung, Diagnose oder Behandlung. Sie enthält keine Heilversprechen. Für Ihre persönliche Situation ist ausschließlich Ihr behandelndes Team zuständig.",
  faqTitle: "Häufige Fragen zu diesem Thema",
  faqQuestions: [
    "Ist eine Ti-Base oder ein individuelles Abutment besser?",
    "Titan oder Zirkonoxid – was ist geeigneter?",
    "Was passiert, wenn sich das Zahnfleisch um das Abutment zurückzieht?",
    "Was ist eine Klebefuge am Abutment?",
  ],
  updatedLabel: "Stand der Seite: 15. September 2026",
};

const en = {
  metaTitle: "Ti-Base or Custom Abutment? A Patient Guide",
  metaDescription:
    "Ti-base, custom abutment, titanium or zirconia: a neutral explanation of what separates the designs and which questions to ask your dentist.",
  breadcrumb: "Abutment guide",
  h1: "Ti-Base or Custom Abutment? A Guide for Patients",
  intro:
    "When an implant receives a crown, a connecting part sits in between: the abutment. Several designs and materials exist for this part. This page explains the differences factually and without ranking them, so you can have a better conversation with your dental team.",
  shortTitle: "The short answer",
  shortAnswer:
    "There is no single design that is right for every patient. Ti-bases with a bonded crown, custom abutments in titanium or zirconia, and stock abutments are all used clinically. Which one fits you depends on implant position, gingival height, occlusion, aesthetic demands, cleanability and the laboratory workflow. That decision belongs to your dental team together with you, not to a manufacturer.",
  basicsTitle: "What an abutment is",
  basics: [
    { term: "Implant", text: "The screw in the bone replacing the tooth root. It is not visible in the mouth." },
    { term: "Abutment", text: "The connecting part between implant and crown. It defines how the crown emerges from the gingiva." },
    {
      term: "Emergence profile",
      text: "The shape of the transition from the implant shoulder to the visible crown. It influences how the soft tissue sits and how well the area can be cleaned.",
    },
    {
      term: "Bonding line",
      text: "The joint where two parts are cemented together, for example crown and ti-base. Its position is a design characteristic.",
    },
  ],
  typesTitle: "The common designs",
  types: [
    {
      name: "Stock abutment",
      what: "An industrially prefabricated part in fixed sizes, with limited adjustment in the laboratory.",
      notes:
        "Established, widely available and predictable in cost. Its shape does not follow the individual gingival contour, so compromises can arise in demanding aesthetic sites.",
    },
    {
      name: "Ti-base with bonded crown",
      what: "A short titanium core onto which the crown, usually zirconia or lithium disilicate, is bonded in the laboratory or chairside.",
      notes:
        "Very common in digital workflows. The position of the bonding line, the bonding technique and excess removal are the central technical topics here. Much of this depends on execution rather than product type.",
    },
    {
      name: "Custom zirconia abutment",
      what: "A patient-specific milled ceramic part, often on a titanium base.",
      notes:
        "Frequently chosen for aesthetic reasons with thin soft tissue. Ceramic is more fracture-sensitive than titanium; suitability depends on load and position.",
    },
    {
      name: "Custom titanium abutment",
      what: "A patient-specific titanium part whose geometry is designed for the individual case. EAP® is one of several manufacturer options in this group.",
      notes:
        "Titanium is regarded as mechanically robust and has long-standing documentation. With very thin soft tissue it can show through; this depends on tissue thickness and positioning.",
    },
  ],
  tableTitle: "Orientation table",
  tableNote:
    "The table describes typical characteristics of the designs. It is not a ranking and does not replace individual assessment.",
  tableHead: ["Characteristic", "Stock abutment", "Ti-base + crown", "Custom zirconia", "Custom titanium"],
  tableRows: [
    ["Shape individually adaptable", "limited", "via the crown", "yes", "yes"],
    ["Material at the gingiva", "titanium", "depends on design", "zirconia", "titanium"],
    ["Bonding line present", "usually no", "yes", "depends on build-up", "depends on build-up"],
    ["Mechanical robustness", "high", "depends on design", "material-dependent", "high"],
    ["Digital workflow common", "partly", "yes", "yes", "yes"],
    ["Planned as definitive", "yes", "yes", "yes", "yes"],
  ],
  materialTitle: "Titanium or zirconia?",
  materialText:
    "Both materials are established in implant prosthetics and both have documented fields of use. Titanium is mechanically strong and documented over decades. Zirconia is tooth-coloured and often chosen when show-through should be avoided. A general superiority of one material over the other cannot be derived from the literature; position, load, tissue thickness and processing are what matter.",
  evidenceTitle: "How solid are statements on this topic?",
  evidenceIntro:
    "Not every statement about abutments carries the same weight. These three levels help to classify them, including statements from manufacturers such as ourselves.",
  evidence: [
    {
      level: "Level A – clinical studies",
      text: "Studies in patients with control groups and defined endpoints. Example: meta-analyses on marginal bone loss with definitive versus repeatedly exchanged abutments report differences of roughly 0.2 mm. Such values are study averages, not predictions for an individual case.",
    },
    {
      level: "Level B – laboratory studies and observations",
      text: "Cell studies, material testing and clinical observations without a comparison group. Example: in one study (Wilson 2009), residual cement was found at 81% of the examined sites showing signs of peri-implant disease. That is an association, not proof of causation.",
    },
    {
      level: "Level C – design and manufacturer information",
      text: "Descriptions of how a product is built and what its design aims at. Such information is verifiable but says nothing on its own about clinical outcomes.",
    },
  ],
  questionsTitle: "Eight questions for your dental team",
  questions: [
    "Which abutment design is planned for me, and why this one?",
    "Which material sits against my gingiva?",
    "Is there a bonding line, and where is it relative to the gingival margin?",
    "Will the crown be bonded or screw-retained, and what does that mean for later access?",
    "Is the abutment planned as a definitive restoration or will it be exchanged later?",
    "How well can I clean the area at home?",
    "What happens if my gingiva recedes later on?",
    "Which alternatives would also be possible in my case?",
  ],
  recessionTitle: "If the gingiva recedes later",
  recessionText:
    "Gingival recession can occur with any type of restoration and has many causes. Whether an exposed area can be adjusted and polished afterwards, or whether a remake is more appropriate, depends on the specific design and the clinical situation. Your dentist makes that assessment.",
  transparencyTitle: "Transparency about the source",
  transparencyText:
    "This page is provided by EAP® Produktions- und Patentverwertungs-GmbH, a manufacturer of patient-specific titanium abutments. We have a commercial interest in our product. Statements about EAP® on this page are therefore labelled as design characteristics rather than clinical benefits. We claim neither superiority over other systems nor prevention of disease.",
  linksTitle: "Read on",
  links: [
    { href: "/ai-press#third-opinion", label: "The Third Opinion: 20 patient questions on implant abutments" },
    { href: "/fuer-patienten", label: "For patients: the basics explained" },
    { href: "/research", label: "Literature overview: the publications referenced" },
    { href: "/faq", label: "Frequently asked questions about EAP® Abutments" },
  ],
  disclaimerTitle: "Note",
  disclaimerText:
    "This page provides general information and does not replace dental advice, diagnosis or treatment. It contains no promise of cure. Only your treating team is responsible for your personal situation.",
  faqTitle: "Frequently asked questions on this topic",
  faqQuestions: [
    "Is a ti-base or a custom abutment better?",
    "Titanium or zirconia – which is more suitable?",
    "What happens if the gingiva recedes around the abutment?",
    "What is a bonding line on an abutment?",
  ],
  updatedLabel: "Page last reviewed: 15 September 2026",
};

const AbutmentGuide = () => {
  const { language } = useLanguage();
  const c = language === "de" ? de : en;

  const faqItems = [
    { q: c.faqQuestions[0], a: c.shortAnswer },
    { q: c.faqQuestions[1], a: c.materialText },
    { q: c.faqQuestions[2], a: c.recessionText },
    { q: c.faqQuestions[3], a: c.basics[3].text },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${CANONICAL}#faq`,
    inLanguage: language,
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${CANONICAL}#article`,
    headline: c.h1,
    description: c.metaDescription,
    inLanguage: language,
    mainEntityOfPage: CANONICAL,
    isAccessibleForFree: true,
    dateModified: LAST_UPDATED,
    publisher: { "@id": "https://eap-abutments.com/#organization" },
    about: [
      { "@type": "Thing", name: "Dental implant abutment" },
      { "@type": "Thing", name: "Ti-base" },
      { "@type": "Thing", name: "Emergence profile" },
    ],
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://eap-abutments.com/" },
      { "@type": "ListItem", position: 2, name: c.breadcrumb, item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDescription} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDescription} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>
      <Navigation />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto min-w-0">
            <Breadcrumbs items={[{ label: c.breadcrumb }]} />

            <header className="mt-4 mb-10">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-5 leading-tight break-words">
                {c.h1}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">{c.intro}</p>
              <p className="mt-4 text-sm text-muted-foreground">
                <time dateTime={LAST_UPDATED}>{c.updatedLabel}</time>
              </p>
            </header>

            {/* Direkte Antwort */}
            <Card className="mb-12 border-primary/30 bg-primary/5">
              <CardContent className="p-6">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground mb-3">
                  <Info className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                  {c.shortTitle}
                </h2>
                <p className="text-muted-foreground leading-relaxed">{c.shortAnswer}</p>
              </CardContent>
            </Card>

            {/* Grundbegriffe */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">{c.basicsTitle}</h2>
              <dl className="grid sm:grid-cols-2 gap-4">
                {c.basics.map((b) => (
                  <div key={b.term} className="p-5 rounded-xl border border-border bg-card min-w-0">
                    <dt className="font-semibold text-foreground mb-1">{b.term}</dt>
                    <dd className="text-sm text-muted-foreground leading-relaxed">{b.text}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {/* Bauformen */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">{c.typesTitle}</h2>
              <div className="space-y-4">
                {c.types.map((tp) => (
                  <Card key={tp.name} className="border-border">
                    <CardContent className="p-6 min-w-0">
                      <h3 className="font-semibold text-lg text-foreground mb-2 break-words">{tp.name}</h3>
                      <p className="text-muted-foreground mb-2 leading-relaxed">{tp.what}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{tp.notes}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Tabelle */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">{c.tableTitle}</h2>
              <p className="text-sm text-muted-foreground mb-4">{c.tableNote}</p>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="bg-muted/50">
                      {c.tableHead.map((h) => (
                        <th key={h} scope="col" className="text-left font-semibold text-foreground p-3">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {c.tableRows.map((row) => (
                      <tr key={row[0]} className="border-t border-border">
                        <th scope="row" className="text-left font-medium text-foreground p-3">
                          {row[0]}
                        </th>
                        {row.slice(1).map((cell, i) => (
                          <td key={i} className="p-3 text-muted-foreground">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Material */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">{c.materialTitle}</h2>
              <p className="text-muted-foreground leading-relaxed">{c.materialText}</p>
            </section>

            {/* Evidenzebenen */}
            <section className="mb-12">
              <h2 className="flex items-center gap-2 text-2xl md:text-3xl font-bold text-foreground mb-4">
                <BookOpen className="w-6 h-6 text-primary flex-shrink-0" aria-hidden="true" />
                {c.evidenceTitle}
              </h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">{c.evidenceIntro}</p>
              <div className="space-y-4">
                {c.evidence.map((e) => (
                  <div key={e.level} className="p-5 rounded-xl border border-border bg-muted/20 min-w-0">
                    <h3 className="font-semibold text-foreground mb-1 break-words">{e.level}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{e.text}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Fragen */}
            <section className="mb-12">
              <h2 className="flex items-center gap-2 text-2xl md:text-3xl font-bold text-foreground mb-6">
                <ClipboardList className="w-6 h-6 text-primary flex-shrink-0" aria-hidden="true" />
                {c.questionsTitle}
              </h2>
              <ol className="space-y-3">
                {c.questions.map((q, i) => (
                  <li key={q} className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card min-w-0">
                    <span className="w-7 h-7 rounded-full bg-primary/10 text-primary font-semibold text-sm flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-foreground leading-relaxed">{q}</span>
                  </li>
                ))}
              </ol>
            </section>

            {/* Rezession */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">{c.recessionTitle}</h2>
              <p className="text-muted-foreground leading-relaxed">{c.recessionText}</p>
            </section>

            {/* Sichtbare FAQ – identisch mit dem FAQPage-Schema */}
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">{c.faqTitle}</h2>
              <div className="space-y-4">
                {faqItems.map((item) => (
                  <div key={item.q} className="p-5 rounded-xl border border-border bg-card min-w-0">
                    <h3 className="font-semibold text-foreground mb-2 break-words">{item.q}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Transparenz */}
            <Card className="mb-12 border-border bg-muted/30">
              <CardContent className="p-6">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground mb-2">
                  <HelpCircle className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                  {c.transparencyTitle}
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.transparencyText}</p>
              </CardContent>
            </Card>

            {/* Interne Links */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">{c.linksTitle}</h2>
              <ul className="space-y-2">
                {c.links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      className="inline-flex items-start gap-2 text-primary underline underline-offset-4 hover:no-underline"
                    >
                      <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0" aria-hidden="true" />
                      <span>{l.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            {/* Disclaimer */}
            <Card className="border-muted bg-muted/20">
              <CardContent className="p-6">
                <h2 className="font-semibold text-muted-foreground mb-2">{c.disclaimerTitle}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.disclaimerText}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default AbutmentGuide;
